import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { pdfjs, Document, Page } from "react-pdf";
import { baseUrl } from "../../../services";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

function modalWidthCss() {
  const w = typeof window !== "undefined" ? window.innerWidth : 1200;
  if (w >= 992) return "65vw";
  if (w >= 768) return "85vw";
  return "min(95vw, calc(100vw - 16px))";
}

const TermAndConditionsModal = ({ isOpen, onCancel, data }) => {
  const { t } = useTranslation();
  const { dir } = useSelector((state) => state.language);
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);
  const [modalWidth, setModalWidth] = useState(() => modalWidthCss());
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setNumPages(null);
      setPageWidth(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const syncLayout = () => {
      setModalWidth(modalWidthCss());
      const el = containerRef.current;
      if (el) {
        const cw = el.clientWidth;
        if (cw > 0) setPageWidth(Math.floor(cw));
      }
    };
    syncLayout();
    window.addEventListener("resize", syncLayout);
    window.addEventListener("orientationchange", syncLayout);
    return () => {
      window.removeEventListener("resize", syncLayout);
      window.removeEventListener("orientationchange", syncLayout);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const cw = el.clientWidth;
      if (cw > 0) setPageWidth(Math.floor(cw));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  function onDocumentLoadSuccess({ numPages: total }) {
    setNumPages(total);
  }

  function onDocumentLoadError() {
    setNumPages(null);
  }

  return (
    <Modal
      centered
      title={
        <h1 dir={dir} className="green-text text-center fsize-5 mb-0 mb-md-3">
          {t("auth.termsAndConditions")}
        </h1>
      }
      width={modalWidth}
      bodyStyle={{
        maxHeight: "min(70vh, calc(100dvh - 200px))",
        minHeight: "200px",
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBlock: 8,
      }}
      open={isOpen}
      wrapClassName="terms-modal"
      rootClassName="terms-modal-root"
      onCancel={onCancel}
      closable={true}
      footer={[]}
    >
      <div
        ref={containerRef}
        className="mx-auto d-flex flex-column align-items-center"
        style={{ width: "100%", maxWidth: "100%", minHeight: 0 }}
      >
        <Document
          file={baseUrl + data}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<Spin size="large" />}
          noData={"No data..."}
        >
          {numPages && pageWidth
            ? Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  loading=""
                  width={pageWidth}
                  className="mb-2"
                />
              ))
            : null}
        </Document>
      </div>
    </Modal>
  );
};

export default TermAndConditionsModal;
